import React from "react";
import {Box, Stack} from "@mui/material";
import {ListQuery, ListRequest} from "../../app/api/api";
import {useTranslation} from "react-i18next";
import {LabeledValue} from "../Layout/Page";
import {EMPTY_UI} from "../../variables";
import {AsyncListAutocomplete} from "../input/AsyncAutocomplete/variants";

export interface MultiFieldAsyncSelectorProps<T extends object> {
    query: ListQuery<T, ListRequest>;
    fields: MultiFieldAsyncSelectorFieldEntry<T>[];
    value: T | undefined;
    onChange: (newValue: T | undefined) => void;
    columns?: number;
}

export interface MultiFieldAsyncSelectorFieldEntry<T extends object> {
    fieldName: keyof T;
    labelKey: string;
    isReadOnly?: boolean;
}

export default function MultiFieldAsyncSelector<T extends object>({
    value,
    query,
    fields,
    onChange,
    columns = 1,
}: MultiFieldAsyncSelectorProps<T>) {
    const {t} = useTranslation();
    const rows: MultiFieldAsyncSelectorFieldEntry<T>[][] = [];
    for (let i = 0; i < fields.length / columns; i++) {
        rows.push(fields.slice(i * columns, (i + 1) * columns));
    }

    return (
        <Box sx={{width: "100%", marginTop: "10px"}}>
            <Stack spacing={1}>
                {rows.map((cell) => (
                    <Box sx={{width: "100%"}}>
                        <Stack direction="row" spacing={1}>
                            {cell.map((field) => (
                                <Box sx={{width: "100%"}}>
                                    {field.isReadOnly ? (
                                        <LabeledValue
                                            label={t(field.labelKey)}
                                            content={value ? String(value[field.fieldName]) : EMPTY_UI}
                                            key={field.labelKey}
                                        />
                                    ) : (
                                        <AsyncListAutocomplete
                                            queryHook={query}
                                            label={t(field.labelKey)}
                                            queryField={field.fieldName}
                                            getOptionLabel={(option: string | T) =>
                                                typeof option === "string" ? option : String(option[field.fieldName])
                                            }
                                            sort={String(field.fieldName)}
                                            value={value}
                                            onChange={onChange}
                                        />
                                    )}
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                ))}
            </Stack>
        </Box>
    );
}
